import { useEffect, useState } from 'react';
import { askSetAnalysis, getSetAnalysis, type SetAnalysis as Analysis } from '../account';

/**
 * Why did this set get worse?
 *
 * The tool measures a drop and stops. Explaining it is the work being paid for,
 * and it is the one place here where a model earns its cost — and the easiest
 * place to spend the product's credibility. Asked "why", a model will produce
 * "Google changed its algorithm" without hesitating.
 *
 * So the server only ever hands it facts we hold, requires every finding to cite
 * the facts it rests on, and throws away anything whose numbers are not in those
 * facts. What arrives here has already survived that, which is why findings and
 * actions are shown differently: a finding is a claim about what happened, an
 * action is advice about what to try, and conflating them is how a report starts
 * lying politely.
 */
export default function SetAnalysisPanel({ domain, name, engine }: {
  domain: string;
  name: string;
  engine: 'gemini' | 'openai';
}): JSX.Element {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [canAsk, setCanAsk] = useState(true);
  const [why, setWhy] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [waiting, setWaiting] = useState(false);
  /**
   * The modal has three states, not one.
   *
   * A sentence appearing under a button does not read as "something is
   * happening" — it reads as a note. And "still working, it will be here next
   * time" left as a line on the page is a dead end: the reader has no idea
   * whether to wait, reload, or give up.
   */
  const [modal, setModal] = useState<'none' | 'working' | 'emailed' | 'empty'>('none');

  const load = (): void => {
    void getSetAnalysis(domain, name, engine)
      .then((d) => { setAnalysis(d.analysis); setCanAsk(d.canAsk); setWhy(d.why); })
      .catch(() => setAnalysis(null));
  };
  useEffect(load, [domain, name, engine]);

  /**
   * Poll while it runs.
   *
   * The work is detached on the server, so there is nothing to await. Six checks
   * ten seconds apart covers a normal completion; after that the answer is
   * simply there on the next visit, which is a better outcome than a spinner
   * that never resolves.
   */
  useEffect(() => {
    if (!waiting) return;
    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      void getSetAnalysis(domain, name, engine).then((d) => {
        if (d.analysis && d.analysis.id !== analysis?.id) {
          setAnalysis(d.analysis);
          setCanAsk(d.canAsk);
          setWhy(d.why);
          setWaiting(false);
          setMsg(null);
          // A run that produced nothing has to say so. Closing the modal on it
          // would look identical to nothing having happened at all, after
          // somebody sat and watched a spinner for it.
          setModal(d.analysis.status === 'empty' ? 'empty' : 'none');
        } else if (tries >= 6) {
          setWaiting(false);
          // Hand it over to the email rather than leaving somebody watching a
          // spinner that has already given up.
          setModal('emailed');
        }
      }).catch(() => { setWaiting(false); setModal('none'); });
    }, 10_000);
    return () => clearInterval(t);
  }, [waiting, domain, name, engine, analysis?.id]);

  const ask = (): void => {
    setBusy(true); setMsg(null);
    void askSetAnalysis(domain, name, engine)
      .then(() => { setWaiting(true); setModal('working'); })
      .catch((e) => { setMsg(e instanceof Error ? e.message : 'could not start that'); setModal('none'); })
      .finally(() => setBusy(false));
  };

  return (
    <div className="mt-4 pt-3 border-t border-warm-dark-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="font-mono text-xs uppercase tracking-[0.14em] text-text-secondary/70">
          What changed, and why
        </span>
        {/*
          "explain this" only while there is something new to explain.
          
          The limit is a new run now, not a calendar, so "ask again" on the same
          numbers would produce the same answer at the same cost. What somebody
          actually wants with an explanation in front of them is to put it in a
          message to whoever will act on it.
        */}
        {canAsk ? (
          <button
            onClick={ask}
            disabled={busy || waiting}
            className="font-mono text-xs text-cyan border border-cyan/40 rounded px-2.5 py-1 hover:bg-cyan/10 disabled:opacity-40 transition"
          >explain this</button>
        ) : analysis && analysis.status !== 'empty' ? (
          <button
            onClick={() => {
              const text = [
                `${name} (${domain})`,
                analysis.summary,
                '',
                ...analysis.findings.map((f) => `- ${f.text}`),
                ...(analysis.actions.length ? ['', 'Worth trying:'] : []),
                ...analysis.actions.map((a) => `- ${a.text}${a.why ? ` (${a.why})` : ''}`),
                '',
                `Measured by PageCoder on ${new Date(analysis.createdAt).toLocaleDateString()}.`,
                'The findings are checked against the measurements; the suggestions are not.',
              ].filter((l) => l !== undefined).join('\n');
              void navigator.clipboard.writeText(text)
                .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })
                .catch(() => setMsg('could not copy'));
            }}
            className="font-mono text-xs text-cyan border border-cyan/40 rounded px-2.5 py-1 hover:bg-cyan/10 transition"
          >{copied ? 'copied ✓' : 'copy to clipboard'}</button>
        ) : null}
        {!canAsk && why && (
          <span className="font-mono text-xs text-text-secondary/70">
            re-opens after the next run of this set
          </span>
        )}
      </div>

      {msg && <p className="font-mono text-xs text-text-secondary mt-2">{msg}</p>}

      {analysis?.status === 'empty' && (
        <p className="text-sm text-text-secondary mt-2 max-w-prose">
          This ran on {new Date(analysis.createdAt).toLocaleDateString()} and came back with nothing it
          could stand behind. Every claim it wrote was either too vague to check or rested on numbers that
          are not in your measurements, so none of it was kept. The next run of this set gives it something
          new to read.
        </p>
      )}

      {analysis && analysis.status !== 'empty' && (
        <div className="mt-3">
          {analysis.summary && <p className="text-sm text-text-primary max-w-prose">{analysis.summary}</p>}

          {analysis.findings.length > 0 && (
            <ul className="mt-2 flex flex-col gap-1.5">
              {analysis.findings.map((f, i) => (
                <li key={i} className="text-sm text-text-secondary max-w-prose">
                  <span className="text-cyan/70 font-mono text-xs mr-1.5">·</span>{f.text}
                </li>
              ))}
            </ul>
          )}

          {analysis.actions.length > 0 && (
            <>
              <p className="font-mono text-xs uppercase tracking-[0.14em] text-text-secondary/70 mt-3 mb-1">
                Worth trying
              </p>
              <ul className="flex flex-col gap-1.5">
                {analysis.actions.map((a, i) => (
                  <li key={i} className="text-sm text-text-secondary max-w-prose">
                    <span className="text-text-primary">{a.text}</span>
                    {a.why && <span className="text-text-secondary/70">. {a.why}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}

          <p className="font-mono text-xs text-text-secondary/60 mt-3">
            {new Date(analysis.createdAt).toLocaleString()}
            {/* Said plainly, because the difference is the whole point: the
                findings are checked against the measurements, the suggestions
                are not and cannot be. */}
            {' · '}the findings above are checked against your own measurements; the suggestions are not
          </p>
        </div>
      )}

      {!analysis && !msg && (
        <p className="text-sm text-text-secondary mt-2 max-w-prose">
          Reads what actually moved in this set and says what the numbers support. It will not guess at
          causes it cannot see, so where the data has no answer it says so.
        </p>
      )}

      {modal !== 'none' && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Explaining this set"
        >
          <div className="w-full max-w-md bg-warm-dark border border-warm-dark-2 rounded-xl p-6 text-center">
            {modal === 'working' && (
              <>
                {/* A spinner, because the only honest thing to show while waiting
                    is that something is happening. */}
                <div className="mx-auto mb-4 w-8 h-8 rounded-full border-2 border-cyan/30 border-t-cyan animate-spin" />
                <h3 className="font-serif text-xl mb-2">Reading what moved</h3>
                <p className="text-sm text-text-secondary">
                  Going through this set run by run and checking every claim against your own numbers.
                  A few seconds.
                </p>
              </>
            )}

            {modal === 'emailed' && (
              <>
                <h3 className="font-serif text-xl mb-2">Taking longer than expected</h3>
                <p className="text-sm text-text-secondary mb-5">
                  It is still running. You do not have to wait here, because you will get an email when it is
                  ready, and it will be on this set's trend when you come back.
                </p>
                <button
                  onClick={() => setModal('none')}
                  className="bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2 text-sm hover:opacity-90 transition"
                >OK</button>
              </>
            )}

            {modal === 'empty' && (
              <>
                <h3 className="font-serif text-xl mb-2">Nothing it could support</h3>
                <p className="text-sm text-text-secondary mb-5">
                  It ran, but everything it produced failed the check against your own measurements, so
                  none of it was kept. I would rather show you nothing than something the measurements do not
                  support. There will be more to go on after the next run of this set.
                </p>
                <button
                  onClick={() => setModal('none')}
                  className="bg-cyan text-warm-dark font-medium rounded-lg px-6 py-2 text-sm hover:opacity-90 transition"
                >OK</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
